// src/services/lexAgentClient.ts
import axios from '../api/axios';
import type {
  LexAgentAction,
  LexAgentClarification,
  LexAgentPartialResponse,
  LexAgentTelemetry,
  LexAgentCost,
  LexAgentPlugin,
  LexAgentTool,
  LexAgentState,
  LexAgentHealth,
} from '../types/lexAgent';

// NOTE: Authentication headers are automatically injected by the axios interceptor in /api/axios.ts.
// No need to manually add Authorization headers in each call below.

// Types for requests and responses
// context: Assumed to be a generic key-value object for additional context
export interface LexAgentMessageRequest {
  sessionId: string;
  userId: string;
  message: string;
  context?: Record<string, unknown>;
  pluginIds?: string[];
  deviceId?: string;  // Cross-device echo filtering (Phase 8)
}

// Persistent device ID for cross-device sync echo filtering
const DEVICE_ID_KEY = 'webelieve-device-id';
let _deviceId: string | null = null;

export function getDeviceId(): string {
  if (_deviceId) return _deviceId;
  _deviceId = sessionStorage.getItem(DEVICE_ID_KEY);
  if (!_deviceId) {
    _deviceId = `web-${crypto.randomUUID()}`;
    sessionStorage.setItem(DEVICE_ID_KEY, _deviceId);
  }
  return _deviceId;
}

// See src/types/lexAgent.ts for all type assumptions
export interface LexAgentMessageResponse {
  intent: string;
  response: string;
  renderHint?: 'markdown' | 'text';
  // Optional structured UI payloads (Phase 4C+)
  renderType?: 'card';
  cardType?: string;
  card?: {
    type: string;
    title: string;
    description?: string;
    data?: Record<string, unknown>;
    actions?: Array<{
      label: string;
      action: 'confirm' | 'reschedule' | 'cancel' | 'open' | 'edit';
      payload?: any;
    }>;
  };
  actions?: LexAgentAction[];
  clarification?: LexAgentClarification;
  partial?: LexAgentPartialResponse;
  telemetry?: LexAgentTelemetry;
  cost?: LexAgentCost;
}

// New interfaces for Chat UI Enhancement endpoints
export interface IncompleteWorkflow {
  workflowId: string;
  workflowName: string;
  workflowType: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  estimatedCompletion: number;
  createdAt: number;
  updatedAt: number;
  description: string;
  nextActions: string[];
  canResume: boolean;
}

export interface WorkflowStatistics {
  totalActive: number;
  totalPaused: number;
  totalInProgress: number;
  averageProgress: number;
}

export interface GlobalWorkflowStatistics {
  totalActiveWorkflows: number;
  workflowsByType: Record<string, number>;
  workflowsByStatus: Record<string, number>;
  averageCompletionTime: number;
}

export interface IncompleteWorkflowsResponse {
  workflows: IncompleteWorkflow[];
  statistics: WorkflowStatistics;
  globalStatistics: GlobalWorkflowStatistics;
}

export interface TelemetryEvent {
  type: 'card_click' | 'card_hover' | 'workflow_resume' | 'workflow_pause' | 'keyboard_shortcut' | 'voice_command' | 'tone_change' | 'context_preview' | 'intent_suggestion_click' | 'plugin_activation' | 'chat_interaction' | 'ui_interaction';
}

export interface TelemetryData {
  event: TelemetryEvent;
  data: Record<string, unknown>;
}

export interface TelemetryResponse {
  success: boolean;
  eventId: string;
  timestamp: number;
  message: string;
}

export interface WorkflowProgress {
  workflowId: string;
  progress: number;
  currentStep: string;
  status: string;
  estimatedCompletion: number;
  nodeStatuses: any[];
}

export interface ResumeWorkflowResponse {
  success: boolean;
  workflowId: string;
  status: string;
  progress: WorkflowProgress;
  message: string;
}


// Sends a message to the LexIntentionSpecialistAgent backend
// Chat turns can include multiple LLM/tool hops (compound orchestration + slot filling setup)
// and regularly exceed the global axios 10s timeout.
const LEX_AGENT_MESSAGE_TIMEOUT_MS = 120000;

function buildPrimerContext(base?: Record<string, unknown>): Record<string, unknown> {
  const context = (base && typeof base === 'object') ? { ...base } : {};
  const timezone =
    typeof context.timezone === 'string' && context.timezone.trim().length > 0
      ? context.timezone
      : (typeof window !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC');
  const locale =
    typeof context.locale === 'string' && context.locale.trim().length > 0
      ? context.locale
      : (typeof navigator !== 'undefined' ? navigator.language : undefined);
  const surface =
    typeof context.surface === 'string' && context.surface.trim().length > 0
      ? context.surface
      : 'web';

  return {
    ...context,
    timezone,
    ...(locale ? { locale } : {}),
    surface,
    surfaceCapabilities: {
      supportsPush: false,
      supportsFileUpload: true,
      supportsVoice: false,
      supportsDeepLinks: false,
      ...(typeof (context as any)?.surfaceCapabilities === 'object'
        ? ((context as any).surfaceCapabilities as Record<string, unknown>)
        : {}),
    },
  };
}

export async function sendMessage(
  payload: LexAgentMessageRequest
): Promise<LexAgentMessageResponse> {
  const context = buildPrimerContext(payload.context);
  const res = await axios.post('/lex-agent/message', {
    ...payload,
    context,
    deviceId: payload.deviceId || getDeviceId(),
  }, {
    timeout: LEX_AGENT_MESSAGE_TIMEOUT_MS,
    headers: {
      'x-client-surface': String(context.surface || 'web'),
      'x-timezone': String(context.timezone || 'UTC'),
      'x-client-timezone': String(context.timezone || 'UTC'),
    },
  });
  return res.data;
}


// Sends a clarification response to the backend
export async function sendClarification(
  payload: { sessionId: string; clarification: string }
): Promise<{ response: string }> {
  const res = await axios.post('/lex-agent/clarify', payload);
  return res.data;
}


// Fetches available plugins from the backend
export async function getPlugins(): Promise<LexAgentPlugin[]> {
  const res = await axios.get('/lex-agent/plugins');
  return res.data;
}


// Fetches the current agent state
export async function getState(): Promise<LexAgentState> {
  const res = await axios.get('/lex-agent/state');
  return res.data;
}


// Fetches the agent health status
export async function getHealth(): Promise<LexAgentHealth> {
  const res = await axios.get('/lex-agent/health');
  return res.data;
}


// Fetches available MCP tools
export async function getTools(): Promise<LexAgentTool[]> {
  const res = await axios.get('/lex-agent/tools');
  return res.data;
}

// === CHAT UI ENHANCEMENT ENDPOINTS ===

// Fetches user's incomplete workflows for the resume card
export async function getIncompleteWorkflows(): Promise<IncompleteWorkflowsResponse> {
  const res = await axios.get('/lex-agent/incomplete-workflows');
  return res.data;
}

// Tracks telemetry events for analytics
export async function trackTelemetry(payload: TelemetryData): Promise<TelemetryResponse> {
  const res = await axios.post('/lex-agent/telemetry', payload);
  return res.data;
}

// Resumes a paused workflow
export async function resumeWorkflow(workflowId: string): Promise<ResumeWorkflowResponse> {
  const res = await axios.post(`/lex-agent/workflows/${workflowId}/resume`);
  return res.data;
}

// === RESPONSE FEEDBACK ===

export interface LexFeedbackRequest {
  conversationId: string;
  messageId: string;
  userQuestion: string;
  agentResponse?: string;
  refinedResponse?: string;
  rating: 'up' | 'down';
  correction?: string;
  intentCategory?: string;
  toolId?: string;
  /** Whether the tool call succeeded — enables feedback classification */
  toolSuccess?: boolean;
}

export interface LexFeedbackResponse {
  success: boolean;
  feedbackId: string;
  rating: 'up' | 'down';
}

export async function submitFeedback(
  payload: LexFeedbackRequest,
): Promise<LexFeedbackResponse> {
  const res = await axios.post('/lex-agent/feedback', payload);
  return res.data;
}

// Convenience function to track card interactions
export async function trackCardInteraction(
  cardType: string,
  action: 'click' | 'hover' | 'keyboard_shortcut',
  additionalData?: Record<string, unknown>
): Promise<TelemetryResponse> {
  return trackTelemetry({
    event: { type: action === 'click' ? 'card_click' : action === 'hover' ? 'card_hover' : 'keyboard_shortcut' },
    data: {
      cardType,
      action,
      timestamp: Date.now(),
      ...additionalData
    }
  });
}
